import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarClock, CirclePlus, Pencil, Trash2, X } from "lucide-react";
import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { DateInput } from "../components/DateInput";
import { ErrorScreen, LoadingScreen } from "../components/ScreenState";
import { api, type HomeworkInput } from "../lib/api";
import { queryClient } from "../lib/query";
import type { Homework } from "../lib/types";

const SUBJECTS = ["语文", "数学", "英语", "科学", "美术", "书法", "劳动", "体育", "综合", "其他"];
const UNITS = ["项", "页", "题", "个", "篇", "分钟", "张", "首", "份", "本", "小时", "次"];

function asInput(homework: Homework): HomeworkInput {
  return {
    childId: homework.childId,
    subject: homework.subject,
    title: homework.title,
    total: homework.total,
    unit: homework.unit,
    startDate: homework.startDate,
    targetDate: homework.targetDate,
    excludedWeekdays: homework.excludedWeekdays,
    excludedDates: homework.excludedDates,
  };
}

export function HomeworkPage() {
  const data = useQuery({ queryKey: ["bootstrap"], queryFn: api.bootstrap });
  const [editing, setEditing] = useState<{ id: string; form: HomeworkInput } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const update = useMutation({
    mutationFn: () => api.updateHomework(editing!.id, editing!.form),
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteHomework(id),
    onSuccess: () => {
      setDeletingId(null);
      void queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    },
  });

  if (data.isPending) return <LoadingScreen />;
  if (data.isError) return <ErrorScreen message={data.error.message} onRetry={() => void data.refetch()} />;
  const deleting = data.data.homeworks.find((item) => item.id === deletingId);

  function submitEdit(event: FormEvent) {
    event.preventDefault();
    update.mutate();
  }

  function patchEdit(patch: Partial<HomeworkInput>) {
    setEditing((current) => current ? { ...current, form: { ...current.form, ...patch } } : current);
  }

  return (
    <section className="screen">
      <PageHeader title="作业" />
      <div className="section-toolbar">
        <p>共 {data.data.homeworks.length} 项进行中</p>
        <Link to="/capture" className="small-button"><CirclePlus size={18} /> 新建</Link>
      </div>
      <div className="open-list">
        {data.data.homeworks.map((item) => (
          <article className="homework-row" key={item.id}>
            <div className="subject-mark">{item.subject.slice(0, 1)}</div>
            <div className="homework-row-copy">
              <div className="row-title"><strong>{item.subject}</strong><span>{item.total} {item.unit}</span></div>
              <p>{item.title}</p>
              <small><CalendarClock size={14} /> {item.targetDate} 截止 · {item.childName}</small>
            </div>
            <div className="homework-row-tools">
              <span className={item.hasActivePlan ? "status success" : "status"}>{item.hasActivePlan ? "已计划" : "待计划"}</span>
              <span className="homework-actions">
                <button type="button" aria-label={`修改：${item.title}`} onClick={() => setEditing({ id: item.id, form: asInput(item) })}><Pencil size={17} /></button>
                <button type="button" aria-label={`删除：${item.title}`} onClick={() => setDeletingId(item.id)}><Trash2 size={17} /></button>
              </span>
            </div>
          </article>
        ))}
        {data.data.homeworks.length === 0 ? <div className="empty-state compact"><h2>还没有作业</h2><p>从录入页开始创建第一项作业。</p></div> : null}
      </div>

      {editing ? (
        <div className="detail-backdrop" role="presentation">
          <section className="detail-sheet homework-edit-sheet" role="dialog" aria-modal="true" aria-labelledby="homework-edit-title">
            <button className="detail-close" type="button" aria-label="关闭" onClick={() => setEditing(null)}><X /></button>
            <span className="detail-icon"><Pencil size={27} /></span>
            <h2 id="homework-edit-title">修改作业</h2>
            <p className="detail-copy">保存后会按新内容重新生成当前计划，历史完成记录仍保留。</p>
            <form className="homework-form" onSubmit={submitEdit}>
              <label className="field full"><span>孩子</span><select value={editing.form.childId} onChange={(event) => patchEdit({ childId: event.target.value })}>{data.data.children.map((child) => <option key={child.id} value={child.id}>{child.nickname} · {child.grade || "未设置年级"}</option>)}</select></label>
              <label className="field full"><span>作业内容</span><textarea value={editing.form.title} maxLength={2000} onChange={(event) => patchEdit({ title: event.target.value })} required /><small>{editing.form.title.length}/2000</small></label>
              <div className="form-grid">
                <label className="field"><span>科目</span><select value={editing.form.subject} onChange={(event) => patchEdit({ subject: event.target.value })}>{SUBJECTS.map((subject) => <option key={subject}>{subject}</option>)}</select></label>
                <label className="field"><span>单位</span><select value={editing.form.unit} onChange={(event) => patchEdit({ unit: event.target.value })}>{UNITS.map((unit) => <option key={unit}>{unit}</option>)}</select></label>
                <label className="field"><span>总量</span><input type="number" min="1" max="10000" value={editing.form.total} onChange={(event) => patchEdit({ total: Number(event.target.value) })} required /></label>
                <label className="field"><span>开始日期</span><DateInput value={editing.form.startDate} max={editing.form.targetDate} onChange={(event) => patchEdit({ startDate: event.target.value })} required /></label>
                <label className="field"><span>截止日期</span><DateInput value={editing.form.targetDate} min={editing.form.startDate} onChange={(event) => patchEdit({ targetDate: event.target.value })} required /></label>
              </div>
              {update.isError ? <p className="form-error" role="alert">{update.error.message}</p> : null}
              <div className="dialog-actions"><button className="secondary-button" type="button" onClick={() => setEditing(null)}>取消</button><button className="small-button" type="submit" disabled={update.isPending}>{update.isPending ? "保存中…" : "保存修改"}</button></div>
            </form>
          </section>
        </div>
      ) : null}

      {deleting ? (
        <div className="detail-backdrop" role="presentation">
          <section className="detail-sheet" role="alertdialog" aria-modal="true" aria-labelledby="homework-delete-title">
            <span className="detail-icon danger"><Trash2 size={28} /></span>
            <h2 id="homework-delete-title">删除这项作业？</h2>
            <p className="detail-copy">“{deleting.title}”及其计划、完成记录会永久删除，无法恢复。</p>
            {remove.isError ? <p className="form-error" role="alert">{remove.error.message}</p> : null}
            <div className="dialog-actions"><button className="secondary-button" type="button" onClick={() => setDeletingId(null)}>取消</button><button className="danger-button" type="button" disabled={remove.isPending} onClick={() => remove.mutate(deleting.id)}>{remove.isPending ? "删除中…" : "确认删除"}</button></div>
          </section>
        </div>
      ) : null}
    </section>
  );
}
